import stringifySafe from 'json-stringify-safe';
import { Checkout } from '@wix/ambassador-ecom-v1-checkout/types';
import { PublicPlan as PlanV2 } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { queryPlans } from '@wix/ambassador-pricing-plans-v3-plan/http';
import { PlanOptions, Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { checkoutStage, pricingPlansClientAuditLogSrc92Evid408 } from '@wix/bi-logger-pricing-plans-data/v2';
import {
  commonIntegrationDataToQuery,
  IntegrationData,
  isCustomPurchaseFlow,
  TpaPageId,
} from '@wix/pricing-plans-router-utils';
import { allowsFutureStartDateV3 } from '@wix/pricing-plans-utils';
import { IWixAPI, ControllerFlowAPI, HttpClient, ViewerScriptFlowAPI } from '@wix/yoshi-flow-editor';
import { PRICING_PLANS_APP_DEF_ID } from '../constants';
import { CheckoutStage } from '../types/common';
import { ECOM_APP_DEF_ID } from '../utils/ecom';
import { toError } from '../utils/errors';
import { getSubPagePath } from '../utils/navigation';
import { EcomService } from './ecom-server';

type PlanV2OrPlanId = { planId: string; planV2?: never } | { planId?: never; planV2: PlanV2 };

export class EcomClient {
  private ecomService: EcomService;
  private wixCodeApi: IWixAPI;
  private flowAPI: ControllerFlowAPI | ViewerScriptFlowAPI;
  private widgetType: string;

  constructor(params: { wixCodeApi: IWixAPI; flowAPI: ControllerFlowAPI | ViewerScriptFlowAPI; widgetType: string }) {
    this.wixCodeApi = params.wixCodeApi;
    this.flowAPI = params.flowAPI;
    this.widgetType = params.widgetType;
    this.ecomService = new EcomService(this.flowAPI);
  }

  async onSelectPlan(
    params: {
      integrationData: IntegrationData;
      checkoutFlowId: string | undefined;
    } & PlanV2OrPlanId,
  ) {
    const { planId, planV2, integrationData, checkoutFlowId } = params;
    const planData = planV2 ? planV2 : await this.fetchPlanV3(planId, checkoutFlowId);
    const isCustomCheckoutFormEnabled = !!planData.formId;
    const isCustomStartDateEnabled = isPlanV3(planData)
      ? allowsFutureStartDateV3(planData)
      : planData.allowFutureStartDate;
    const shouldGoToCustomization = isCustomCheckoutFormEnabled || isCustomStartDateEnabled;
    if (shouldGoToCustomization) {
      return this.navigateToCustomizationPage({ planId: planData.id!, integrationData, checkoutFlowId });
    }

    const planV3 = isPlanV3(planData) ? planData : await this.fetchPlanV3(planData.id!, checkoutFlowId);
    const checkout = await this.createCheckout({ plan: planV3, integrationData, checkoutFlowId });
    return this.navigateToCheckout({ checkout, plan: planV3, integrationData, checkoutFlowId });
  }

  async createAndNavigateToCheckout(params: {
    plan: PlanV3;
    submissionId?: string | null;
    startDate?: string | null;
    integrationData: IntegrationData;
    checkoutFlowId: string | undefined;
  }) {
    const { plan, submissionId, startDate, integrationData, checkoutFlowId } = params;
    const checkout = await this.createCheckout({ plan, submissionId, startDate, integrationData, checkoutFlowId });
    return this.navigateToCheckout({ checkout, plan, integrationData, checkoutFlowId });
  }

  private async createCheckout(params: {
    plan: PlanV3;
    submissionId?: string | null;
    startDate?: string | null;
    integrationData: IntegrationData;
    checkoutFlowId: string | undefined;
  }): Promise<Checkout> {
    const { plan, integrationData, submissionId, startDate, checkoutFlowId } = params;
    const auditLogData = { planId: plan.id!, checkoutFlowId, widgetType: this.widgetType };
    const REQUEST_NAME = 'createCheckout';
    try {
      this.reportRequestStatus({
        entityType: 'checkout',
        entityId: '',
        name: REQUEST_NAME,
        status: 'started',
        data: auditLogData,
      });
      const checkout = await this.ecomService.createCheckout(
        params.plan.id!,
        this.getPlanOptions({ plan, integrationData, submissionId, startDate }),
        checkoutFlowId,
      );
      this.reportRequestStatus({
        entityType: 'checkout',
        entityId: checkout.id!,
        name: REQUEST_NAME,
        status: 'finished',
        data: auditLogData,
      });
      return checkout;
    } catch (error) {
      this.reportRequestStatus({
        entityType: 'checkout',
        entityId: '',
        name: REQUEST_NAME,
        status: 'failed',
        data: auditLogData,
        error,
      });
      throw error;
    }
  }

  private async fetchPlanV3(planId: string, checkoutFlowId: string | undefined): Promise<PlanV3> {
    const REQUEST_NAME = 'fetchPlanV3';
    const auditLogData = { checkoutFlowId, widgetType: this.widgetType };
    try {
      this.reportRequestStatus({
        entityType: 'plan',
        entityId: planId,
        name: REQUEST_NAME,
        status: 'started',
        data: auditLogData,
      });
      const response = await this.flowAPI.httpClient.request(queryPlans({ query: { filter: { id: planId } } }));
      const plan = response.data.plans![0];
      if (!plan) {
        throw new Error('Plan not found');
      }
      this.reportRequestStatus({
        entityType: 'plan',
        entityId: planId,
        name: REQUEST_NAME,
        status: 'finished',
        data: auditLogData,
      });
      return plan;
    } catch (error) {
      this.reportRequestStatus({
        entityType: 'plan',
        entityId: planId,
        name: REQUEST_NAME,
        status: 'failed',
        error,
        data: auditLogData,
      });
      throw error;
    }
  }

  private async navigateToCheckout(data: {
    checkout: Checkout;
    plan: PlanV3;
    integrationData: IntegrationData;
    checkoutFlowId: string | undefined;
  }) {
    const { checkout, plan, integrationData, checkoutFlowId } = data;
    const successUrl = await this.getCustomSuccessPageUrl({
      checkoutId: checkout.id!,
      planId: plan.id!,
      integrationData,
    });
    this.reportNavigationStart({
      planId: plan.id!,
      isCustomCheckoutFormEnabled: !!plan.formId,
      isCustomStartDateEnabled: allowsFutureStartDateV3(plan),
      checkoutFlowId,
    });
    const ecomPublicApi = await this.getEcomPublicApi(this.wixCodeApi);
    return ecomPublicApi.navigate.toCheckout({
      checkoutId: checkout.id!,
      disableContinueShopping: true,
      successUrl,
    });
  }

  private async navigateToCustomizationPage(data: {
    planId: string;
    integrationData: IntegrationData;
    checkoutFlowId: string | undefined;
  }) {
    const { planId, integrationData, checkoutFlowId } = data;
    const path = getSubPagePath(
      {
        name: 'customization',
        planCustomizationData: {
          planId,
          integrationData,
          checkoutFlowId,
        },
      },
      { isMultiPageApp: true },
    );
    const customizationPage = await this.wixCodeApi.site.getSectionUrl({
      appDefinitionId: PRICING_PLANS_APP_DEF_ID,
      sectionId: TpaPageId.PlanCustomization,
    });
    if (!customizationPage.relativeUrl) {
      throw new Error(
        'Cannot navigate to plan customization, because Plan Customization page is not available on the site.',
      );
    }

    this.wixCodeApi.location.to!(customizationPage.relativeUrl + path);
  }

  private getPlanOptions(data: {
    plan: PlanV3;
    integrationData: IntegrationData;
    submissionId?: string | null;
    startDate?: string | null;
  }): PlanOptions {
    const { plan, integrationData, submissionId, startDate } = data;
    const pricingVariantId = plan.pricingVariants![0].id!;
    const membersOnly = isCustomPurchaseFlow(integrationData);
    const options: PlanOptions = {
      pricingVariantId,
      membersOnly,
    };

    if (submissionId) {
      options.submissionId = submissionId;
    }

    if (startDate) {
      options.startDate = new Date(startDate);
    }

    return options;
  }

  private async getCustomSuccessPageUrl(params: {
    checkoutId: string;
    planId: string;
    integrationData: IntegrationData;
  }): Promise<string | undefined> {
    const { checkoutId, planId, integrationData } = params;
    const { relativeUrl } = await this.wixCodeApi.site.getSectionUrl({
      appDefinitionId: PRICING_PLANS_APP_DEF_ID,
      sectionId: TpaPageId.ThankYou,
    });
    if (!isCustomPurchaseFlow(integrationData)) {
      return;
    }

    if (!relativeUrl) {
      return;
    }

    const continuePagePath = relativeUrl?.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
    const baseUrl = this.wixCodeApi.location.baseUrl;
    const queryParams = commonIntegrationDataToQuery(integrationData);
    queryParams.set('isEcom', 'true');
    queryParams.set('checkoutId', checkoutId);
    queryParams.set('planId', planId);
    return encodeURIComponent(`${baseUrl}${continuePagePath}?${queryParams.toString()}`);
  }

  private getEcomPublicApi(wixCodeApi: IWixAPI) {
    return wixCodeApi.site.getPublicAPI(ECOM_APP_DEF_ID);
  }

  private reportNavigationStart(params: {
    planId: string;
    isCustomCheckoutFormEnabled: boolean;
    isCustomStartDateEnabled: boolean;
    checkoutFlowId?: string;
  }) {
    const { planId, isCustomCheckoutFormEnabled, isCustomStartDateEnabled, checkoutFlowId } = params;
    this.flowAPI.bi?.report(
      checkoutStage({
        stage: CheckoutStage.ECOM_CHECKOUT_NAVIGATION_START,
        checkoutFlowId,
        isCustomCheckoutFormEnabled: Boolean(isCustomCheckoutFormEnabled).toString(),
        isCustomStartDateEnabled: Boolean(isCustomStartDateEnabled).toString(),
        planGuid: planId,
      }),
    );
  }

  private reportRequestStatus(params: {
    entityType: string;
    entityId: string;
    status: 'started' | 'finished' | 'failed';
    name: string;
    data?: Record<string, any>;
    error?: unknown;
  }) {
    const { entityType, entityId, status, name: transaction, data, error } = params;
    let errorData: Record<string, any> | undefined;
    if (status === 'failed' && error) {
      errorData = HttpClient.isHttpError(error)
        ? {
            message: error.message,
            requestId: error.requestId,
          }
        : { message: toError(error).message };
    }

    return this.flowAPI.bi?.report(
      pricingPlansClientAuditLogSrc92Evid408({
        entityType,
        entityId,
        message: `${transaction} - ${status}`,
        data: stringifySafe({
          ...data,
          error: errorData,
        }),
      }),
    );
  }
}

function isPlanV3(plan: PlanV2 | PlanV3): plan is PlanV3 {
  return 'pricingVariants' in plan;
}
